import { Box, Flex, Icon, Input, Skeleton, Text, useColorModeValue } from '@chakra-ui/react';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { MdVideocam } from 'react-icons/md';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { READ_FAILED_I18N_KEY } from '../../_shared/readState';
import { VideoInfo } from '../types';
import { translate } from '../../../i18n';

interface Props {
  videoSrc: string | null;
  videoRef: RefObject<HTMLVideoElement>;
  videoInfo: VideoInfo;
  onVideoLoaded?: (src: string) => void;
  isLoading?: boolean;
  onVideoError?: () => void;
  /**
   * The read that would have produced `videoSrc` was issued and refused.
   *
   * Without it this panel has exactly three states — playing, loading, and
   * "No video provided" — so a recording whose `hasVideo` is true and whose
   * SAS-URL read 403s or times out falls into the third and tells the operator
   * the clip does not exist. That is the run-97 class on a video: a definitive
   * claim made about a question that came back empty-handed. Optional and
   * defaulted off, so the upload modal (the caller that passes
   * `onVideoLoaded`) is unchanged.
   */
  isUnreadable?: boolean;
}

export default function VideoPlayerPanel({
  videoSrc,
  videoRef,
  videoInfo,
  onVideoLoaded,
  isLoading,
  onVideoError,
  isUnreadable,
}: Props) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const placeholderBg = useColorModeValue('gray.100', 'navy.700');
  const borderColor = useColorModeValue('gray.300', 'gray.600');
  const [videoUrl, setVideoUrl] = useState('');
  const blobUrlRef = useRef<string | null>(null);
  // Bounds retries to one attempt per playback session, not per distinct
  // src: a re-fetched SAS URL always differs (fresh sig/expiry) even when
  // the underlying blob is still unreachable, so comparing src strings
  // would never block a genuinely broken video and retry forever. Resets
  // only once a video actually starts playing, so a later, unrelated
  // expiry (hours on) still gets its own single retry.
  const hasRetriedRef = useRef(false);

  const handleVideoError = useCallback(() => {
    if (!onVideoError || hasRetriedRef.current) return;
    hasRetriedRef.current = true;
    onVideoError();
  }, [onVideoError]);

  const handleVideoLoaded = useCallback(() => {
    hasRetriedRef.current = false;
  }, []);

  // Revoke previous blob URL to prevent memory leaks
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const onDrop = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (!file || !onVideoLoaded) return;
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const url = URL.createObjectURL(file);
      blobUrlRef.current = url;
      onVideoLoaded(url);
    },
    [onVideoLoaded],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'video/*': ['.mp4', '.webm', '.mov'] },
    multiple: false,
    noClick: false,
  });

  const loadUrl = () => {
    if (videoUrl.trim() && onVideoLoaded) {
      onVideoLoaded(videoUrl.trim());
      setVideoUrl('');
    }
  };

  return (
    <Box>
      {videoSrc ? (
        <video
          ref={videoRef}
          controls
          style={{ width: '100%', maxHeight: '400px', borderRadius: '8px' }}
          src={videoSrc}
          onError={handleVideoError}
          onLoadedData={handleVideoLoaded}
        >
          Your browser does not support the video tag.
        </video>
      ) : isLoading ? (
        <Skeleton height="200px" borderRadius="8px" />
      ) : isUnreadable ? (
        // Ahead of both placeholders below, including the editable dropzone:
        // "we could not fetch it" outranks both "drop one here" and "there
        // isn't one", because only the first is true.
        <Flex h="200px" align="center" justify="center" borderRadius="8px" bg={placeholderBg} px={4}>
          <InlineErrorState message={translate(READ_FAILED_I18N_KEY)} />
        </Flex>
      ) : onVideoLoaded ? (
        <Box
          {...getRootProps()}
          h="200px"
          borderRadius="8px"
          bg={placeholderBg}
          border="2px dashed"
          borderColor={borderColor}
          cursor="pointer"
          _hover={{ borderColor: 'brand.500' }}
        >
          <input {...getInputProps()} />
          <Flex h="100%" direction="column" align="center" justify="center" gap={2}>
            <Icon as={MdVideocam} w={8} h={8} color="secondaryGray.600" />
            <Text color="secondaryGray.600" fontWeight="500">{translate('court_recording.drop_video')}</Text>
            <Flex align="center" gap={2} mt={1} onClick={(e) => e.stopPropagation()}>
              <Input
                size="xs"
                borderRadius="6px"
                placeholder={translate('court_recording.paste_url')}
                w="220px"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') loadUrl();
                }}
              />
            </Flex>
          </Flex>
        </Box>
      ) : (
        <Flex
          h="200px"
          align="center"
          justify="center"
          borderRadius="8px"
          bg={placeholderBg}
        >
          <Text color="secondaryGray.600">{translate('court_recording.no_video')}</Text>
        </Flex>
      )}
      <Flex mt={2} gap={4} flexWrap="wrap">
        <Text fontSize="xs" color={textColor}>
          {videoInfo.width}x{videoInfo.height} @ {videoInfo.fps.toFixed(1)} fps
        </Text>
        <Text fontSize="xs" color={textColor}>
          {videoInfo.duration_seconds.toFixed(1)}s ({videoInfo.total_frames} frames)
        </Text>
        <Text fontSize="xs" color="secondaryGray.600">{videoInfo.filename}</Text>
      </Flex>
    </Box>
  );
}
